// ============================================================
// Yoklama geçmişi ve giriş/çıkış kaydı (§4)
//
// Tohum veride yoklama tek günün objesiydi (branch.attendance).
// Devamsızlık raporu üretmek, ücret iadesi tartışmasına dayanak
// bulmak ve yangın anında binada kimin olduğunu söyleyebilmek için
// tarihli bir kayda ihtiyaç var.
//
// branch.attendanceLog = { "2026-08-05": { lf1: {...}, ... } }
// Bugünün kaydı branch.attendance ile AYNI nesnedir — eski kod
// bozulmadan çalışsın diye referans paylaşılır.
//
// Firestore karşılığı:
//   branches/{tid}/attendance/{YYYY-MM-DD}/records/{studentId}
// ============================================================

import { BUGUN } from "../config.js";
import { seedHash } from "../lib/avatar.js";
import { B, tumSubeler } from "./context.js";

export const HAFTA_SONU = [0, 6];   // Pazar, Cumartesi

// Tarih aritmetiği UTC üzerinden yapılır. Yerel saatle kurup toISOString()
// ile geri okumak, UTC'nin doğusundaki saat dilimlerinde (KKTC UTC+3) günü
// bir geri kaydırıyor — gunEkle aynı günü döndürüp sonsuz döngü üretiyordu.
const utc = iso => new Date(iso + "T00:00:00Z");

export const okulGunu = iso => !HAFTA_SONU.includes(utc(iso).getUTCDay());

export function gunEkle(iso, adim) {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + adim);
  return d.toISOString().slice(0, 10);
}

/** Bir dönemin (YYYY-MM) okul günleri, bugünü geçmeyecek şekilde. */
export function donemGunleri(period) {
  const gunler = [];
  const son = utc(`${period}-01`);
  son.setUTCMonth(son.getUTCMonth() + 1);
  const sonIso = son.toISOString().slice(0, 10);

  for (let g = `${period}-01`; g < sonIso; g = gunEkle(g, 1)) {
    if (g > BUGUN) break;
    if (okulGunu(g)) gunler.push(g);
  }
  return gunler;
}

// ---------- Okuma ----------

export const gunlukKayit = (t, gun) => t.attendanceLog?.[gun] ?? null;

export const ogrenciKaydi = (t, gun, studentId) => gunlukKayit(t, gun)?.[studentId] ?? null;

/** Kayıtlı tüm günler, yeniden eskiye. */
export const kayitliGunler = t => Object.keys(t.attendanceLog ?? {}).sort().reverse();

/**
 * Şu anda binada olan çocuklar — geldi işaretli ve henüz çıkışı girilmemiş.
 * Yangın/tahliye anında bakılacak tek liste budur.
 */
export function binadakiler(t = B()) {
  const bugun = gunlukKayit(t, BUGUN) ?? {};
  return t.students.filter(s => {
    const a = bugun[s.id];
    return a?.status === "geldi" && !a.out;
  });
}

/** Bir öğrencinin dönem devam özeti. */
export function aylikOzet(t, student, period) {
  const gunler = donemGunleri(period);
  const sayac = { geldi: 0, gelmedi: 0, izinli: 0, kayitsiz: 0 };

  for (const g of gunler) {
    const a = ogrenciKaydi(t, g, student.id);
    if (!a) sayac.kayitsiz++;
    else sayac[a.status] = (sayac[a.status] ?? 0) + 1;
  }

  const islenen = gunler.length - sayac.kayitsiz;
  return {
    gunler,
    ...sayac,
    islenen,
    devamOrani: islenen ? Math.round((sayac.geldi / islenen) * 100) : null,
  };
}

/** Sınıf bazlı dönem özeti — devamsızlık deseni buradan görünür. */
export function sinifOzeti(t, classId, period) {
  const kids = t.students.filter(s => s.classId === classId);
  const ozetler = kids.map(s => ({ student: s, ozet: aylikOzet(t, s, period) }));
  const oranlar = ozetler.map(x => x.ozet.devamOrani).filter(x => x != null);
  return {
    ozetler: ozetler.sort((a, b) => (a.ozet.devamOrani ?? 100) - (b.ozet.devamOrani ?? 100)),
    ortalama: oranlar.length ? Math.round(oranlar.reduce((a, b) => a + b, 0) / oranlar.length) : null,
  };
}

// ---------- Geçmiş üretimi ----------
//
// Tohum veri yalnızca bugünü taşıyor. Aylık görünümün ve devamsızlık
// raporunun boş kalmaması için geçmiş günler faturaGecmisiUret() ile
// aynı mantıkta — deterministik, kimlikten türeyen — üretilir.

const GECMIS_GUN = 45;

const SAAT = (h, dk) => `${String(h).padStart(2, "0")}:${String(dk).padStart(2, "0")}`;

function gunUret(t, gun) {
  const kayit = {};
  for (const s of t.students) {
    // Öğrenci kayıt tarihinden önce okulda değildi.
    if (gun < s.enrolledAt) continue;

    const h = seedHash(s.id + gun);
    const r = h % 100;

    // ~%88 geldi, %7 izinli, %5 gelmedi — gerçekçi bir kreş devam oranı.
    let status = "geldi";
    if (r >= 88 && r < 95) status = "izinli";
    else if (r >= 95) status = "gelmedi";

    if (status !== "geldi") {
      kayit[s.id] = {
        status,
        in: null,
        out: null,
        reason: status === "izinli"
          ? ["Aile ziyareti", "Doktor randevusu", "Hastalık", "İzinli"][h % 4]
          : null,
        by: null,
        outBy: null,
      };
      continue;
    }

    const girisDk = 20 + ((h >>> 3) % 45);           // 08:20 – 09:05
    const yarim = s.schedule === "yarim";
    const cikisSaat = yarim ? 13 : 16;
    const cikisDk = (h >>> 7) % 50;

    kayit[s.id] = {
      status: "geldi",
      in: SAAT(8, girisDk),
      out: SAAT(cikisSaat, cikisDk),
      reason: yarim ? "Yarım gün" : null,
      by: null,          // geçmiş kayıtlarda teslim eden bilinmiyor
      outBy: null,
    };
  }
  return kayit;
}

/**
 * Her şube için geçmiş yoklama günlerini üretir ve bugünün kaydını
 * mevcut branch.attendance nesnesine bağlar.
 */
export function yoklamaGecmisiUret() {
  for (const t of tumSubeler()) {
    t.attendanceLog ??= {};

    // Bugün: aynı nesne paylaşılır, eski kod bozulmaz.
    t.attendanceLog[BUGUN] = t.attendance;

    // Bugünün kayıtlarına teslim alanlar için alan aç.
    for (const a of Object.values(t.attendance)) {
      a.by ??= null;
      a.outBy ??= null;
    }

    let gun = gunEkle(BUGUN, -1);
    for (let i = 0; i < GECMIS_GUN; i++) {
      if (okulGunu(gun) && !t.attendanceLog[gun]) t.attendanceLog[gun] = gunUret(t, gun);
      gun = gunEkle(gun, -1);
    }
  }
}
